import { useEffect } from 'react';
import {
  Button,
  ColorInput,
  Divider,
  Drawer,
  FileInput,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPhoto } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, rawRequest, unwrap } from '../../api/client';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

type FormValues = {
  name: string;
  short_name: string;
  city: string;
  address: string;
  postal_code: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  primary_color: string;
  head_instructor: string;
  notes: string;
  logo: File | null;
  icao_code: string;
  iata_code: string;
  local_identifier: string;
  elevation_ft: number | '';
  radio_frequency: string;
  operating_hours: string;
  approval_type: string;
  approval_number: string;
  competent_authority: string;
  head_of_training: string;
};

/**
 * Editing an organisation, including whichever kind profile it has.
 *
 * The airport's ICAO code and the school's approval number live on separate profile records,
 * but to whoever is filling this in they are simply more fields about the same place.
 */
export function OrganisationFormDrawer({
  opened,
  onClose,
  slug,
}: {
  opened: boolean;
  onClose: () => void;
  slug: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['organisation', slug],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/organisations/{slug}/', { params: { path: { slug } } })),
    enabled: opened,
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      short_name: '',
      city: '',
      address: '',
      postal_code: '',
      country: 'ME',
      email: '',
      phone: '',
      website: '',
      primary_color: '#1c7ed6',
      head_instructor: '',
      notes: '',
      logo: null,
      icao_code: '',
      iata_code: '',
      local_identifier: '',
      elevation_ft: '',
      radio_frequency: '',
      operating_hours: '',
      approval_type: '',
      approval_number: '',
      competent_authority: '',
      head_of_training: '',
    },
    validate: {
      name: (value) => (value.trim() ? null : t('common.requiredField')),
    },
  });

  const record = detail.data;

  useEffect(() => {
    if (!record) return;
    const airport = record.airport_profile;
    const school = record.school_profile;
    form.setValues({
      name: record.name,
      short_name: record.short_name ?? '',
      city: record.city ?? '',
      address: record.address ?? '',
      postal_code: record.postal_code ?? '',
      country: record.country ?? '',
      email: record.email ?? '',
      phone: record.phone ?? '',
      website: record.website ?? '',
      primary_color: record.primary_color ?? '#1c7ed6',
      head_instructor: record.head_instructor ? String(record.head_instructor) : '',
      notes: record.notes ?? '',
      logo: null,
      icao_code: airport?.icao_code ?? '',
      iata_code: airport?.iata_code ?? '',
      local_identifier: airport?.local_identifier ?? '',
      elevation_ft: airport?.elevation_ft ?? '',
      radio_frequency: airport?.radio_frequency ?? '',
      operating_hours: airport?.operating_hours ?? '',
      approval_type: school?.approval_type ?? '',
      approval_number: school?.approval_number ?? '',
      competent_authority: school?.competent_authority ?? '',
      head_of_training: school?.head_of_training ? String(school.head_of_training) : '',
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      unwrap(
        await api.PATCH('/api/v1/organisations/{slug}/', {
          params: { path: { slug } },
          body: {
            name: values.name.trim(),
            short_name: values.short_name.trim(),
            city: values.city.trim(),
            address: values.address.trim(),
            postal_code: values.postal_code.trim(),
            country: values.country.toUpperCase(),
            email: values.email.trim(),
            phone: values.phone.trim(),
            website: values.website.trim(),
            primary_color: values.primary_color,
            head_instructor: values.head_instructor ? Number(values.head_instructor) : null,
            notes: values.notes,
          },
        }),
      );

      if (record?.airport_profile) {
        unwrap(
          await api.PATCH('/api/v1/airport-profiles/{id}/', {
            params: { path: { id: record.airport_profile.id } },
            body: {
              icao_code: values.icao_code.toUpperCase(),
              iata_code: values.iata_code.toUpperCase(),
              local_identifier: values.local_identifier.toUpperCase(),
              elevation_ft: values.elevation_ft === '' ? null : values.elevation_ft,
              radio_frequency: values.radio_frequency,
              operating_hours: values.operating_hours,
            },
          }),
        );
      }

      if (record?.school_profile) {
        unwrap(
          await api.PATCH('/api/v1/school-profiles/{id}/', {
            params: { path: { id: record.school_profile.id } },
            body: {
              approval_type: values.approval_type as never,
              approval_number: values.approval_number,
              competent_authority: values.competent_authority,
              head_of_training: values.head_of_training
                ? Number(values.head_of_training)
                : null,
            },
          }),
        );
      }

      if (values.logo) {
        const body = new FormData();
        body.append('logo', values.logo);
        await rawRequest(`/organisations/${slug}/`, { method: 'PATCH', body });
      }
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const peopleOptions = (people.data?.results ?? []).map((person) => ({
    value: String(person.id),
    label: person.full_name,
  }));

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={record?.display_name ?? t('nav.organisation')}
    >
      {detail.isLoading ? (
        <Loader />
      ) : (
        <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
          <Stack gap="md">
            <Group grow align="flex-start">
              <TextInput label={t('common.name')} required {...form.getInputProps('name')} />
              <TextInput
                label={t('settings.shortName')}
                w={140}
                {...form.getInputProps('short_name')}
              />
            </Group>

            <Group grow align="flex-start">
              <TextInput label={t('staff.email')} {...form.getInputProps('email')} />
              <TextInput label={t('staff.phone')} {...form.getInputProps('phone')} />
            </Group>

            <Group grow align="flex-start">
              <TextInput label={t('staff.address')} {...form.getInputProps('address')} />
              <TextInput label={t('staff.city')} {...form.getInputProps('city')} />
              <TextInput
                label={t('organisation.postalCode')}
                w={120}
                {...form.getInputProps('postal_code')}
              />
            </Group>

            <TextInput label={t('organisation.website')} {...form.getInputProps('website')} />

            <Select
              label={t('organisation.headInstructor')}
              data={peopleOptions}
              searchable
              clearable
              {...form.getInputProps('head_instructor')}
            />

            <Group grow align="flex-start">
              <ColorInput
                label={t('organisation.primaryColor')}
                format="hex"
                swatches={[
                  '#1c7ed6',
                  '#0b7285',
                  '#2f9e44',
                  '#e8590c',
                  '#c2255c',
                  '#5f3dc4',
                  '#343a40',
                ]}
                {...form.getInputProps('primary_color')}
              />
              <FileInput
                label={t('organisation.logo')}
                leftSection={<IconPhoto size={15} />}
                accept="image/*"
                clearable
                {...form.getInputProps('logo')}
              />
            </Group>

            {record?.airport_profile ? (
              <>
                <Divider label={t('nav.airport')} labelPosition="left" />
                <Group grow align="flex-start">
                  <TextInput
                    label={t('organisation.icaoCode')}
                    maxLength={4}
                    {...form.getInputProps('icao_code')}
                  />
                  <TextInput
                    label={t('organisation.iataCode')}
                    maxLength={3}
                    {...form.getInputProps('iata_code')}
                  />
                  <TextInput
                    label={t('organisation.localIdentifier')}
                    {...form.getInputProps('local_identifier')}
                  />
                </Group>
                <Group grow align="flex-start">
                  <NumberInput
                    label={t('organisation.elevation')}
                    {...form.getInputProps('elevation_ft')}
                  />
                  <TextInput
                    label={t('organisation.radioFrequency')}
                    placeholder="123.500"
                    {...form.getInputProps('radio_frequency')}
                  />
                  <TextInput
                    label={t('organisation.operatingHours')}
                    {...form.getInputProps('operating_hours')}
                  />
                </Group>
              </>
            ) : null}

            {record?.school_profile ? (
              <>
                <Divider label={t('nav.school')} labelPosition="left" />
                <Group grow align="flex-start">
                  <TextInput
                    label={t('organisation.approvalType')}
                    placeholder="DTO"
                    {...form.getInputProps('approval_type')}
                  />
                  <TextInput
                    label={t('organisation.approvalNumber')}
                    {...form.getInputProps('approval_number')}
                  />
                </Group>
                <Group grow align="flex-start">
                  <TextInput
                    label={t('organisation.competentAuthority')}
                    {...form.getInputProps('competent_authority')}
                  />
                  <Select
                    label={t('organisation.headOfTraining')}
                    data={peopleOptions}
                    searchable
                    clearable
                    {...form.getInputProps('head_of_training')}
                  />
                </Group>
              </>
            ) : null}

            <Textarea
              label={t('common.notes')}
              autosize
              minRows={2}
              {...form.getInputProps('notes')}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                {t('action.cancel')}
              </Button>
              <Button type="submit" loading={save.isPending}>
                {t('action.save')}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Drawer>
  );
}
